/* Task-Management Phase 2 / L: label UI — the row/card chip and the task-drawer
 * add/remove editor. All color resolution goes through lib/labelColors.ts
 * (token KEY → `.wsl-<key>` class), so a raw hex can never reach the DOM. The
 * data logic (fetch roster, create, attach/detach) lives on the Workspace
 * instance (state/workspace.ts) and is unit-tested there; this module is the
 * lit rendering half only.
 *
 * L3: any member may create a label (the "+ New label" affordance below is
 * unconditional). Labels are organizational, not attention events — no
 * notification fires on a label edit (decision D-notify). */
import { html, nothing, type TemplateResult } from 'lit';
import { labelColorClass } from '../lib/labelColors.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsLabel, WsTask } from '../types/workspace.ts';

/** A read-only label chip (row/card): a ghost pill with a token-colored dot,
 *  deliberately distinct from the filled status pill and the azure unread
 *  badge. `onRemove` (drawer only) adds a removable ✕. */
export function labelChip(l: WsLabel, onRemove?: () => void): TemplateResult {
  return html`<span class="pill-meta count wslabel ${labelColorClass(l.color)}${onRemove ? ' rm' : ''}"
    title=${onRemove ? `${l.name} — click ✕ to remove` : l.name}>
    <span class="wsldot" aria-hidden="true"></span>${l.name}${
      onRemove
        ? html`<button class="wslx" aria-label="remove label ${l.name}"
            @click=${(ev: MouseEvent) => { ev.stopPropagation(); onRemove(); }}>✕</button>`
        : nothing
    }</span>`;
}

/** A task's chips as an inline row (board/card meta). Empty → nothing. */
export function labelChips(t: WsTask): TemplateResult | typeof nothing {
  const labels = t.labels || [];
  if (!labels.length) return nothing;
  return html`<span class="wslabels">${labels.map((l) => labelChip(l))}</span>`;
}

/** The drawer-expander label editor: current chips (removable) + an "add
 *  existing" select limited to labels not already on the task + a "+ New label"
 *  create-and-attach (L3). `reload` re-renders the expander after a change (the
 *  same collapse+expand the note-save / watch toggles use). Tokens only. */
export function labelEditor(ws: Workspace, t: WsTask, reload: () => void): TemplateResult {
  const attached = new Set((t.labels || []).map((l) => l.id));
  const available = (ws.labels || []).filter((l) => !attached.has(l.id));
  const add = async (id: number) => { await ws.toggleTaskLabel(t, id, true); reload(); };
  const remove = async (id: number) => { await ws.toggleTaskLabel(t, id, false); reload(); };
  const create = async () => {
    const name = await ws.ask({ title: 'New label', hint: 'A short, shared label (e.g. “Legal review”).', ok: 'Create' });
    if (!name || !name.trim()) return;
    const l = await ws.createLabel(name.trim());
    if (l) { await ws.toggleTaskLabel(t, l.id, true); reload(); }
  };
  return html`<div class="wslabeledit">
    <span class="wsllbl">Labels</span>
    ${(t.labels || []).map((l) => labelChip(l, () => remove(l.id)))}
    ${available.length
      ? html`<select class="wsfilter-sel wsladd" aria-label="add an existing label"
          @change=${(ev: Event) => {
            const sel = ev.target as HTMLSelectElement;
            const id = Number(sel.value);
            sel.value = '';
            if (id) add(id);
          }}>
          <option value="">+ label…</option>
          ${available.map((l) => html`<option value="${l.id}">${l.name}</option>`)}
        </select>`
      : nothing}
    <button class="wsmini wslnew" @click=${create}>${ws.ICON.plus}New label</button>
  </div>`;
}
